import { cn, Spinner } from "@tellescope/ui";
import type { CommonCalendarOptions, DateRange } from "../../types";
import type { CalendarFilters } from "../../filters";
import { useCalendarEventsWithFilters } from "../../hooks";
import { useMemo } from "react";
import { getEventsByDate } from "../../utils";
import { DayEventList } from "./DayEventList";
import { useTranslation } from "react-i18next";
import { CalendarX } from "lucide-react";

export interface CalendarListViewProps extends DateRange, CommonCalendarOptions {
  className?: string;
  getOnDayClick?: (date: Date) => (() => void) | string;
  getOnEventClick?: (eventId: string) => (() => void) | { to: string; replace?: boolean };
  filters?: CalendarFilters;
  currentUserId?: string;
}

export const CalendarListView = ({
  startDate,
  referenceDate = startDate,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  className,
  getOnDayClick,
  getOnEventClick,
  weekStart,
  filters,
  currentUserId,
}: CalendarListViewProps) => {
  const { t } = useTranslation("calendar");
  const eventsQuery = useCalendarEventsWithFilters({
    referenceDate,
    view: "month",
    timezone,
    weekStart,
    filters,
    currentUserId,
  });
  type Event = NonNullable<typeof eventsQuery.data>[number];
  const dateList = useMemo<[number, Event[]][]>(() => {
    const map = getEventsByDate(eventsQuery.data ?? [], timezone, true);
    const keys = Object.keys(map).map(Number);
    return keys.map<[number, Event[]]>((key) => [key, map[key] ?? []]).sort(([a], [b]) => a - b);
  }, [eventsQuery.data, timezone]);

  return (
    <section
      data-testid="calendar-view-list"
      className={cn("flex flex-col gap-4 flex-grow overflow-y-auto p-4", className)}
    >
      {eventsQuery.isLoading || !eventsQuery.data?.length ? (
        <div className="flex flex-col items-center justify-center flex-grow text-muted-foreground font-light gap-2">
          {eventsQuery.isLoading ? <Spinner size="md" /> : <CalendarX size={40} />}
          {eventsQuery.isLoading ? t("Loading...") : t("No events scheduled for the selected month")}
        </div>
      ) : (
        dateList.map(([dateTime, events]) => (
          <DayEventList
            key={`list-view-day-${dateTime}`}
            date={new Date(dateTime)}
            events={events}
            onDayClick={getOnDayClick?.(new Date(dateTime))}
            getOnEventClick={getOnEventClick}
            timezone={timezone}
          />
        ))
      )}
    </section>
  );
};
